import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  Signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NgMaterialModule } from '@principle-theorem/ng-material';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import {
  RecurrenceDirection,
  AutomationTiming,
} from '@principle-theorem/principle-core';
import {
  IAutomationTiming,
  TimingDirection,
} from '@principle-theorem/principle-core/interfaces';
import {
  DATE_FORMAT,
  filterUndefined,
  TIME_FORMAT,
  Timezone,
  WEEK_DAYS,
} from '@principle-theorem/shared';
import * as moment from 'moment-timezone';
import { map } from 'rxjs/operators';

interface IAutomationTimingExample {
  appointmentDate: string;
  automationDate: string;
  automationTime?: string;
}

@Component({
  selector: 'pr-automation-timing-examples',
  imports: [NgMaterialModule],
  templateUrl: './automation-timing-examples.component.html',
  styleUrl: './automation-timing-examples.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutomationTimingExamplesComponent {
  private _organisation = inject(OrganisationService);
  private _practiceTimezone = toSignal(
    this._organisation.practice$.pipe(
      filterUndefined(),
      map((practice) => practice.settings.timezone)
    )
  ) as Signal<Timezone>;
  private _startOfWeek = computed(() =>
    moment().tz(this._practiceTimezone()).startOf('isoWeek')
  );

  automationTiming = input.required<IAutomationTiming>();
  timingExamples = computed(() =>
    this._calculateTimingExamples(this.automationTiming())
  );

  private _calculateTimingExamples(
    timing: IAutomationTiming
  ): IAutomationTimingExample[] {
    if (!timing) {
      return [];
    }
    const recurrenceDirection =
      timing.direction === TimingDirection.After
        ? RecurrenceDirection.Forward
        : RecurrenceDirection.Backward;

    return WEEK_DAYS.map((_day, index) => {
      const appointmentDate = this._startOfWeek().clone().add(index, 'days');
      const automationDate = AutomationTiming.getNextValidOccurrence(
        appointmentDate.clone(),
        timing.daysOfWeek,
        recurrenceDirection,
        this._practiceTimezone(),
        timing.countOnlySelectedDays ?? false,
        timing.unit,
        timing.amount
      );

      return {
        appointmentDate: appointmentDate.format(DATE_FORMAT),
        automationDate: automationDate.format(DATE_FORMAT),
        automationTime: timing.time
          ? moment(timing.time, 'HH:mm').format(TIME_FORMAT)
          : undefined,
      };
    });
  }
}
